import { ScreenCalcMethods } from 'store/settings/types';
import type {
  UnitInfoBananaScreenInterface,
  UnitInfoCMScreenInterface,
  UnitInfoFeederScreenInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoMobileScreenInterface,
  UnitInfoMultislopeScreenInterface,
  UnitInfoSchenckScreenInterface,
} from 'store/units/types';
import { ScreenModels } from 'unitData/CircularMotionScreen/CircularMotionScreenData';
import type {
  CircularMotionScreenDeckMapInterface,
  ScreeningMediaInterface,
} from 'unitData/CircularMotionScreen/CircularMotionScreenTypes';
import type { FeederScreenDeckMap } from 'unitData/Feeders/FeederScreen/FeederScreenTypes';
import { UnitTypes } from 'unitData/types';
import type {
  DeckMapEfficiencyInterface,
  DeckMapNormalInterface,
} from '../types';
import type { FreeFallDeckInterface } from 'unitData/FreeFallScreen/sharedTypes';
import type { MultislopeScreenDeckMapInterface } from 'unitData/MultislopeScreen/MultislopeScreenTypes';

export const getGenericScreen1 = (
  mobileScreenUnitInfo: UnitInfoMobileScreenInterface,
) => {
  if (
    mobileScreenUnitInfo.modelName !== 'QE141' &&
    mobileScreenUnitInfo.configuration.configKey !== 1
  ) {
    return {
      type: 'GENERIC_SCREEN',
      decks: {
        0: {
          separationSize:
            mobileScreenUnitInfo.GENERIC_SCREEN1.decks[0].separationSize,
        },
      },
    };
  }

  return {
    type: 'GENERIC_SCREEN',
    decks: {
      0: {
        separationSize:
          mobileScreenUnitInfo.GENERIC_SCREEN1.decks[0].separationSize,
      },
      1: {
        separationSize:
          mobileScreenUnitInfo.GENERIC_SCREEN1.decks[1].separationSize,
      },
    },
  };
};

export const getConventionalScreen1 = (
  unitInfo: UnitInfoMobileScreenInterface,
  type: string,
) => {
  const { modelName, CONVENTIONAL_SCREEN1 } = unitInfo;
  const getDeckForTracked = (index: 0 | 1 | 2) => ({
    separationSize: CONVENTIONAL_SCREEN1.decks[index].separationSize,
    screeningMediaId: CONVENTIONAL_SCREEN1.decks[index].screeningMediaId?.value,
    overUnderSize: CONVENTIONAL_SCREEN1.decks[index].overUnderSize?.value,
  });

  const getDeckForWheeled = (index: 0 | 1 | 2) => ({
    separationSize: CONVENTIONAL_SCREEN1.decks[index].separationSize,
    screeningMediaId: CONVENTIONAL_SCREEN1.decks[index].screeningMediaId?.value,
  });

  let decks: Record<
    number,
    {
      separationSize: number;
      screeningMediaId?: string;
      overUnderSize?: string;
    }
  > = {};
  if (type === UnitTypes.MOBILE_TRACKED_SCREEN) {
    decks = {
      0: getDeckForTracked(0),
      1: getDeckForTracked(1),
    };
    if (modelName.includes('QA451') || modelName.includes('QA452')) {
      decks[2] = getDeckForTracked(2);
    }
  } else {
    decks = {
      0: getDeckForWheeled(0),
      1: getDeckForWheeled(1),
      2: getDeckForWheeled(2),
    };
  }

  if (type === UnitTypes.MOBILE_TRACKED_SCREEN) {
    return {
      type: 'CONVENTIONAL_SCREEN',
      screenFamily: 'SA',
      screenModel: 'SA2164',
      inclination: CONVENTIONAL_SCREEN1.inclination,
      materialType: CONVENTIONAL_SCREEN1.materialType.value,
      deckOneRelieving: CONVENTIONAL_SCREEN1.deckOneRelieving,
      decks: decks,
    };
  }

  return {
    type: 'FREEFALL_SCREEN_SF',
    modelName: 'SF1843',
    rockMaterial: CONVENTIONAL_SCREEN1.materialType.value,
    decks: decks,
  };
};

export const getDeckMap = (
  deckList:
    | UnitInfoKwataniScreenInterface['deckList']
    | UnitInfoSchenckScreenInterface['deckList']
    | UnitInfoBananaScreenInterface['deckList'],
  nbrOfDecks: number,
) => {
  const decks = Object.values(deckList).reduce((acc, curr, i) => {
    const index = i as keyof typeof deckList;
    if (nbrOfDecks === 1 && index === 1) {
      return acc;
    }
    return {
      ...acc,
      [index]: {
        separationSize: curr.separationSize,
        screeningMediaId: curr.screeningElement.value,
        openArea: curr.openArea,
        particleShape: curr.particleShape.value,
        efficiencyParams:
          curr.efficiency.value === 'OTHER'
            ? {
                mode: 'OTHER',
                value: curr.efficiencyNumber,
              }
            : {
                mode: curr.efficiency.value,
              },
      },
    };
  }, {});
  return decks;
};

export const getFeederScreenDeckMap = (
  unitInfo: UnitInfoFeederScreenInterface,
) => {
  const deckMap: FeederScreenDeckMap = {
    '0': {
      separationSize: unitInfo.decks[0].separationSize,
    },
    '1': {
      separationSize: unitInfo.decks[1].separationSize,
    },
  };
  return deckMap;
};

const checkScreeningMedia = (screeningMedia?: ScreeningMediaInterface) => {
  if (screeningMedia) {
    if (screeningMedia.model === ScreenModels.WX1000WM) {
      return 'WX1000_WIRE_MESH';
    } else if (screeningMedia.model === ScreenModels.WX1000HW) {
      return 'WX1000_HARP_WIRE';
    } else if (screeningMedia.model === ScreenModels.WN4000WN5000) {
      return 'WN4000_WN5000';
    } else if (screeningMedia.model === ScreenModels.SCREENEX_40D) {
      return 'SCREENEX_40D';
    } else if (screeningMedia.model === ScreenModels.SCREENEX_60D) {
      return 'SCREENEX_60D';
    } else if (screeningMedia.model === ScreenModels.SCREENEX_65D) {
      return 'SCREENEX_65D';
    } else if (screeningMedia.model === ScreenModels.SCREENEX_80D) {
      return 'SCREENEX_80D';
    } else {
      return screeningMedia.model;
    }
  }
};

export const getCMDeckMap = (
  unitInfo: UnitInfoCMScreenInterface,
  nbrOfDecks: number,
  screenCalcMethod: string,
) => {
  if (screenCalcMethod === ScreenCalcMethods.EFFICIENCY.value) {
    let deckMapEfficiency: DeckMapEfficiencyInterface = {};
    for (let i = 0; i < nbrOfDecks; i++) {
      const index = i as keyof CircularMotionScreenDeckMapInterface;
      deckMapEfficiency = {
        ...deckMapEfficiency,
        [i]: {
          separationSize: unitInfo.deckList[index].separationSize,
          screeningMediaId: unitInfo.deckList[index].screeningElement.value,
          openArea: unitInfo.deckList[index].openArea ?? 0,
          particleShape: unitInfo.deckList[index].particleShape.value,
          efficiencyParams: {
            mode: unitInfo.deckList[index].efficiency.value,
            value: unitInfo.deckList[index].efficiencyNumber ?? 0,
          },
        },
      };
    }
    return deckMapEfficiency;
  } else {
    let deckMapNormal: DeckMapNormalInterface = {};
    for (let i = 0; i < nbrOfDecks; i++) {
      const index = i as keyof CircularMotionScreenDeckMapInterface;
      deckMapNormal = {
        ...deckMapNormal,
        [i]: {
          separationSize: unitInfo.deckList[index].separationSize,
          screeningMediaId:
            checkScreeningMedia(unitInfo.deckList[index].screeningMedia) ?? '',
          overUnderSize:
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            unitInfo.deckList[index].maxAmountOfOverUnderSize?.value ||
            'NOT_PRODUCT',
        },
      };
    }
    return deckMapNormal;
  }
};

export const getMultislopeDeckMap = (
  unitInfo: UnitInfoMultislopeScreenInterface,
  nbrOfDecks: number,
  _screenCalcMethod: string,
) => {
  let deckMapEfficiency: DeckMapEfficiencyInterface = {};
  for (let i = 0; i < nbrOfDecks; i++) {
    const index = i as keyof MultislopeScreenDeckMapInterface;
    deckMapEfficiency = {
      ...deckMapEfficiency,
      [i]: {
        separationSize: unitInfo.deckList[index].separationSize,
        screeningMediaId:
          checkScreeningMedia(unitInfo.deckList[index].screeningMedia) ?? '',
        apertureRatio: unitInfo.deckList[index].apertureRatio,
        typeOfApplication: unitInfo.deckList[index].typeOfApplication.value,
        sprayBar: unitInfo.deckList[index].sprayBar,
        openArea: unitInfo.deckList[index].openArea ?? 0,
        particleShape: unitInfo.deckList[index].particleShape,
        efficiencyParams: {
          mode: unitInfo.deckList[index].efficiency.value,
          value: unitInfo.deckList[index].efficiencyNumber ?? 0,
        },
      },
    };
  }
  return deckMapEfficiency;
};

export const getFreefallDecks = (
  deckList: Record<number, FreeFallDeckInterface>,
  nbrOfDecks: number,
) => {
  return Object.values(deckList).reduce((acc, curr, index) => {
    if (index >= nbrOfDecks) return acc;
    return {
      ...acc,
      [index]: {
        separationSize: curr.separationSize,
        screeningMediaId: curr.screeningMediaId.value,
      },
    };
  }, {});
};
